import { useCallback } from 'react';
import { Button, Card, Form, Space } from 'antd';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import { useResource } from 'app/common/api';
import { AppPage } from 'app/common/app-page/features/app-page';
import {
  NotificationsRender,
  useNotifyFactory,
} from 'app/common/notifications/features/notifications';
import { UserRoleList } from 'app/railways/domains/access/features/roles/role-list';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';
import { TUser } from 'app/railways/domains/users/core/user';
import { addUserRequest } from 'app/railways/domains/users/requests/add-user.request';
import { appRoute } from 'app/railways/router/routes';

import { Loading, PageContent } from '../../../../ui';
import { UserFields } from './user-fields';

type NewUser = Omit<TUser, 'id'>;

export const AddUserPage = observer(() => {
  const navigate = useNavigate();
  const authStatusStore = useAuthStatusWriter();
  const notify = useNotifyFactory();
  const addUserResource = useResource<NewUser, TUser>({
    request: addUserRequest,
    errorNotify: notify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone: ({ id }) => {
      const newUserPath = appRoute.USER.DETAILS.buildPath({ id });
      navigate(newUserPath);
    },
  });

  const isLoading = addUserResource.isLoading;

  const handleFormSubmit = useCallback(
    (newUser: Omit<TUser, 'id'>) => {
      addUserResource.send(newUser);
    },
    [addUserResource],
  );

  return (
    <AppPage perm="PERM_USER_CREATE">
      <PageContent>
        <Form
          layout="vertical"
          onFinish={handleFormSubmit}
          scrollToFirstError={{ behavior: 'smooth' }}
        >
          <Loading active={isLoading}>
            <Space
              direction="vertical"
              size="middle"
              style={{ display: 'flex' }}
            >
              <Card size="small" title="Данные входа">
                <UserFields />
              </Card>

              <Card size="small" title="Роли и разрешения">
                <p>
                  Выберите необходимое роли, которые будут назначены
                  пользователю
                </p>
                <Form.Item
                  name="roles"
                  rules={[
                    {
                      required: true,
                      message: 'Пожалуйста, выберите необходимые роли',
                    },
                  ]}
                >
                  <UserRoleList />
                </Form.Item>
              </Card>

              <NotificationsRender notifyStore={notify} />

              <Card size="small">
                <div style={{ display: 'flex' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ marginLeft: 'auto' }}
                  >
                    Добавить пользователя
                  </Button>
                </div>
              </Card>
            </Space>
          </Loading>
        </Form>
      </PageContent>
    </AppPage>
  );
});
